import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref } from 'vue';
import { notification } from 'ant-design-vue';
import ColorModuleHeader from '@/modules/ColorModule/components/ColorModuleHeader.vue';
import ColorModuleTable from '@/modules/ColorModule/components/ColorModuleTable.vue';
import ColorModuleRequest from '@/modules/ColorModule/components/ColorModuleRequest.vue';
import ColorModuleResult from '@/modules/ColorModule/components/ColorModuleResult.vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import Color from '@/modules/ColorModule/api/models/Color';
import LatestColor from '@/modules/ColorModule/api/models/LatestColor';
import { ColorData } from '@/modules/ColorModule/interfaces/ColorData';
import {
    formatCustomColor,
    formatColorSearchQuery,
    FiltersConfiguration,
    MappedColorCollections,
} from '@/modules/ColorModule/helpers/ColorModuleHelper';
import { Color as ColorInfo } from '@/modules/ColorModule/interfaces/Color';
import { ColorRequestConfig } from '@/modules/ColorModule/interfaces/ColorRequestConfig';
import { ColorFilters } from '@/modules/ColorModule/interfaces/ColorFilters';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { formatSelectedColorForCustomRequest } from '@/modules/ColorModule/helpers/ColorModuleHelper';
import ColorModuleTitle from '@/modules/ColorModule/components/ColorModuleTitle.vue';
import ColorModuleWelcomeModal from '@/modules/ColorModule/components/ColorModuleWelcomeModal.vue';
import { ColorPowder } from '@/modules/ColorModule/interfaces/ColorPowder';
import { validChecklistFieldsForColorModule as fields } from '@/regex/validChecklistFieldsForColorModule';
import store from '@/store';
import i18n from '@/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'ColorModule',
  setup(__props) {

const { proxy } = getCurrentInstance()!;
const visible = ref(false);
const infoModalVisible = ref(false);
const pagination = ref({
    total: 10,
    current: 1,
    pageSizeOptions: ['10', '20', '50', '100'],
    showSizeChanger: true,
    pageSize: 10,
});
const colorSystemPID = ref('');
const manualInputFieldValue = ref('ROČNI VPIS');
const dropdownValueWhereCMWasOpened = ref('');
const activeProductFormId = ref<number | null>(0);
const activeTab = ref('color-search');
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const filters = ref<ColorFilters>({
    colorGloss: { id: '', value: '' },
    colorSurface: { id: '', value: '' },
    colorQuality: { id: '', value: '' },
    colorEffect: { id: '', value: '' },
    colorSystem: { id: '', value: '' },
    colorCollection: { id: '', value: '' },
});
const requestForm = ref<ColorRequestConfig>({
    colorName: '',
    colorProducer: '',
    powderID: '',
});

const updateRequestForm = (key: keyof ColorRequestConfig, value: string) => {
    requestForm.value = {
        ...requestForm.value,
        [key]: value,
    };
};
const validationErrors = ref<{ [key: string]: string }>({
    colorName: '',
    colorProducer: '',
    powderID: '',
});
const searchQuery = ref('');
const filtersActive = ref(false);
const requestFiltersActive = ref(false);
const showLatestColors = ref(false);
const moduleLoaded = ref(false);
const productName = ref('');
const directory = ref('');

const paginationData = computed(() => (moduleLoaded.value ? pagination.value : false));
const checklistPIDs = computed(() => {
    switch (colorSystemPID.value) {
        case fields.SYSTEM_COLOR:
            return {
                manualColorSelectionPID: 'p74022',
                standardColorsDropdownPID: 'p74019',
                nonStandardColorsDropdownPID: 'p74020',
            };
        case fields.COLOR_OF_THE_FINISHING_LAMELLA:
            return {
                manualColorSelectionPID: 'p1035',
                standardColorsDropdownPID: 'p1032',
                nonStandardColorsDropdownPID: 'p1033',
            };
        case fields.COLOR_OF_THE_INSECT_SCREEN:
            return {
                manualColorSelectionPID: 'p10067',
                standardColorsDropdownPID: 'p10064',
                nonStandardColorsDropdownPID: 'p10065',
            };
        case fields.OMARICA_ROLLER_SHUTTER_BOX_COLOR:
            return {
                manualColorSelectionPID: 'p40182',
                standardColorsDropdownPID: 'p40179',
                nonStandardColorsDropdownPID: 'p40180',
            };
        case fields.OMARICA_BACK_COLOR:
            return {
                manualColorSelectionPID: 'p10383',
                standardColorsDropdownPID: 'p10380',
                nonStandardColorsDropdownPID: 'p10381',
            };
        case fields.COLOR_OF_THE_FLUSHMOUNTED_F_CORNER:
            return {
                manualColorSelectionPID: 'p9094',
                standardColorsDropdownPID: 'p9091',
                nonStandardColorsDropdownPID: 'p9092',
            };
        case fields.OMARICA_SHEET_METAL_COLOR:
            return {
                manualColorSelectionPID: 'p110213',
                standardColorsDropdownPID: 'p110210',
                nonStandardColorsDropdownPID: 'p110211',
            };
        case fields.COLOR_OF_THE_FLUSHMOUNTED_CORNER:
            return {
                manualColorSelectionPID: 'p110220',
                standardColorsDropdownPID: 'p110217',
                nonStandardColorsDropdownPID: 'p110218',
            };
        case fields.VODILA_GUIDE_COLOR:
            return {
                manualColorSelectionPID: 'p40123',
                standardColorsDropdownPID: 'p40120',
                nonStandardColorsDropdownPID: 'p40121',
            };
        case fields.COLOR_OF_THE_MOSQUITOS_NET_GUIDES:
            return {
                manualColorSelectionPID: 'p82706',
                standardColorsDropdownPID: 'p82703',
                nonStandardColorsDropdownPID: 'p82704',
            };
        case fields.OMARICA_SERVICE_COVER_COLOR:
            return {
                manualColorSelectionPID: 'p10389',
                standardColorsDropdownPID: 'p10386',
                nonStandardColorsDropdownPID: 'p10388',
            };
        case fields.OMARICA_COVER_COLOR:
            return {
                manualColorSelectionPID: 'p10370',
                standardColorsDropdownPID: 'p10367',
                nonStandardColorsDropdownPID: 'p10368',
            };
        case fields.MASKA:
            return {
                manualColorSelectionPID: 'p9088',
                standardColorsDropdownPID: 'p9085',
                nonStandardColorsDropdownPID: 'p9084',
            };
        case fields.BLIND_COLOR:
            return {
                manualColorSelectionPID: 'p740581',
                standardColorsDropdownPID: 'p740578',
                nonStandardColorsDropdownPID: 'p740579',
            };
        default:
            return {
                manualColorSelectionPID: 'p74022',
                standardColorsDropdownPID: 'p74019',
                nonStandardColorsDropdownPID: 'p74020',
            };
    }
});

const requestColor = computed(() => formatCustomColor(requestForm.value, filters.value));
const currentCollection = computed(() => filters.value.colorCollection.value);

const openPopup = async (payload?: {
    colorSystemPID: string;
    fieldValue: string;
    activeProductFormId: number;
    productName?: string;
    productCategory?: string;
}) => {
    if (payload) {
        colorSystemPID.value = payload.colorSystemPID;
        dropdownValueWhereCMWasOpened.value = payload.fieldValue;
        activeProductFormId.value = payload.activeProductFormId;
        productName.value = payload.productName || '';
        directory.value = payload.productCategory || '';
    }

    visible.value = true;

    if (!moduleLoaded.value) {
        loadModule();
    }
};

const loadModule = async () => {
    try {
        loadingOverlay.start();
        moduleLoaded.value = true;
        registerInfoModal();

        await fetchAndRegisterColorsData();
        setDefaultValues();
        await fetchColors();
    } catch {
        return Promise.reject();
    } finally {
        loadingOverlay.stop();
    }
};

const registerInfoModal = () => {
    const infoModalShown = localStorage.getItem('colorModuleInfoModalShown');

    if (!infoModalShown) {
        infoModalVisible.value = true;
        localStorage.setItem('colorModuleInfoModalShown', 'true');
    }
};

const closePopup = () => {
    visible.value = false;
    // moduleLoaded.value = false;
    // reset forms
    // requestForm.value = {
    //     colorName: '',
    //     colorProducer: '',
    //     powderID: '',
    // };
    // filters.value = {
    //     colorGloss: { id: '', value: '' },
    //     colorSurface: { id: '', value: '' },
    //     colorQuality: { id: '', value: '' },
    //     colorEffect: { id: '', value: '' },
    //     colorSystem: { id: '', value: '' },
    //     colorCollection: { id: '', value: '' },
    // };
};

const onTableConfigChange = (newPagination: any, tableFilters: any, sorter: any) => {
    pagination.value.current = newPagination.current as number;
    pagination.value.pageSize = newPagination.pageSize as number;
};

const setPaginationData = (totalItems: number) => {
    pagination.value.total = totalItems;
};

const changeActiveTab = (tab: string) => {
    activeTab.value = tab;
};

const setFiltersActiveState = (active: boolean) => {
    if (activeTab.value === 'color-request') {
        requestFiltersActive.value = active;
        return;
    }

    filtersActive.value = active;
};

const clearFilters = () => {
    // check if filters are already cleared
    if (!Object.values(filters.value).some((filter) => filter.id || filter.value) && searchQuery.value === '') {
        return;
    }

    filters.value = {
        colorGloss: { id: '', value: '' },
        colorSurface: { id: '', value: '' },
        colorQuality: { id: '', value: '' },
        colorEffect: { id: '', value: '' },
        colorSystem: { id: '', value: '' },
        colorCollection: { id: '', value: '' },
    };

    searchQuery.value = '';
    fetchColors();
};

const selectColor = (colorData: {
    color: ColorInfo;
    selectedPowder: ColorPowder;
    isRoltekColor: boolean;
    groupName: keyof typeof MappedColorCollections;
}) => {
    LatestColor.createNew({
        colorId: colorData?.color?.id || null,
        powderId: colorData?.selectedPowder?.id || null,
    });

    if (colorSystemPID.value === 'material') {
        EventBus.$emit(EventBusEvents.selectMaterialColor, colorData);
        closePopup();
        return;
    }

    if (!colorData?.isRoltekColor) {
        const formattedColor = formatSelectedColorForCustomRequest(colorData.color, colorData.selectedPowder);
        sendCustomColorRequest(formattedColor, true);

        return;
    }

    const fieldGroup = MappedColorCollections[colorData.groupName];

    const colorDropdownPID =
        colorData.groupName === 'STANDARD'
            ? checklistPIDs.value.standardColorsDropdownPID
            : checklistPIDs.value.nonStandardColorsDropdownPID;

    const payload = {
        activeProductFormId: activeProductFormId.value,
        colorSystemPID: colorSystemPID.value,
        colorDropdownPID: colorDropdownPID,
        colorCollection: fieldGroup,
        selectedColorValue: colorData.color.ingeniousColor,
    };

    emitColorChange(payload);
    closePopup();
};

const sendCustomColorRequest = (selectedColor: string = '', shouldFreezeManualInput: boolean = false) => {
    const validateFields = validateRequestForm();

    if (!validateFields && activeTab.value === 'color-request') {
        notification.error({
            message: 'Validation Error',
            description: Object.values(validationErrors.value)
                .filter((val) => !!val)
                .join('\n'),
        });
        return;
    }

    const payload = {
        activeProductFormId: activeProductFormId.value,
        colorSystemPID: colorSystemPID.value,
        colorDropdownPID: checklistPIDs.value.manualColorSelectionPID,
        colorCollection: manualInputFieldValue.value,
        selectedColorValue: selectedColor || requestColor.value,
        shouldFreezeManualInput: shouldFreezeManualInput,
    };

    emitColorChange(payload);
    closePopup();
};

const emitColorChange = (payload: any) => {
    if (activeProductFormId.value === null) {
        EventBus.$emit(EventBusEvents.updateMultipositionHeaderSystemColorValue, payload);
    } else {
        EventBus.$emit(EventBusEvents.updateSystemColorValue, payload);
    }
};

const validateRequestForm = () => {
    if (!requestForm.value.colorName) {
        validationErrors.value.colorName = i18n.t('Color name is required!') as string;
    } else {
        validationErrors.value.colorName = '';
    }

    if (!requestForm.value.colorProducer) {
        validationErrors.value.colorProducer = (i18n.t('Color producer is required!') + ' ' + i18n.t('or')) as string;
    } else {
        validationErrors.value.colorProducer = '';
    }

    if (!requestForm.value.powderID) {
        validationErrors.value.powderID = i18n.t("Manufacturer's powder ID is required!") as string;
    } else {
        validationErrors.value.powderID = '';
    }

    return !Object.values(validationErrors.value).some((error) => error);
};

const onFilterChange = ({ value, type, id }: { value: string; type: keyof ColorFilters; id: string }) => {
    filters.value[type] = { id, value };

    if (activeTab.value === 'color-search') {
        fetchColors();
    }
};

const searchColors = ({ query }: { query: string }) => {
    searchQuery.value = query;
};

const toggleLatestColors = async (val: boolean) => {
    showLatestColors.value = val;

    if (val === true) {
        const colors = await LatestColor.getAll();
        const latestColors = await store.dispatch('colorData/setLatestColors', { latestColors: colors });

        // Set pagination total to the number of latest colors
        setPaginationData(latestColors);
    }
};

const fetchColors = async () => {
    // Since powders are the most important part of the color module, we will fetch them instead of colors and include colors in the response
    try {
        loadingOverlay.start();

        const query = formatColorSearchQuery(filters.value, '');
        const [latestColors, colors] = await Promise.all([LatestColor.getAll(), Color.getPowders(query)]);

        store.dispatch('colorData/setColorsData', { colors });
        store.dispatch('colorData/setLatestColors', { latestColors });

        setPaginationData(colors.length);
    } catch (error) {
        console.error(error);
    } finally {
        loadingOverlay.stop();
    }
};

const fetchAndRegisterColorsData = async () => {
    const requests: Promise<ColorData>[] = [
        Color.getPowderPrices(),
        Color.getSurfaces(),
        Color.getQualities(),
        Color.getEffects(),
        Color.getColorCharts(),
        Color.getColorCollections(),
    ];

    try {
        const results = await Promise.allSettled(requests);
        const [prices, surfaces, qualities, effects, charts, collections] = results.map((result) =>
            result.status === 'fulfilled' ? result.value : [],
        );

        store.dispatch('colorData/setColorsData', {
            prices,
            surfaces,
            qualities,
            effects,
            charts,
            collections,
        });
    } catch (error) {
        console.error(error);
    }
};

const setDefaultValues = () => {
    FiltersConfiguration.forEach((filter) => {
        if (filter?.defaultValue) {
            const selectedOption = filter.options().find((option: any) => option.id === filter.defaultValue);
            const value = selectedOption?.value || selectedOption?.groupName || '';
            filters.value[filter.type as keyof ColorFilters] = { id: selectedOption.id, value };
        }
    });
};

onMounted(() => {
    EventBus.$on(EventBusEvents.showColorModule, openPopup);
});

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.showColorModule, openPopup);
});

return { __sfc: true,proxy, visible, infoModalVisible, pagination, colorSystemPID, manualInputFieldValue, dropdownValueWhereCMWasOpened, activeProductFormId, activeTab, loadingOverlay, filters, requestForm, updateRequestForm, validationErrors, searchQuery, filtersActive, requestFiltersActive, showLatestColors, moduleLoaded, productName, directory, paginationData, checklistPIDs, requestColor, currentCollection, openPopup, loadModule, registerInfoModal, closePopup, onTableConfigChange, setPaginationData, changeActiveTab, setFiltersActiveState, clearFilters, selectColor, sendCustomColorRequest, emitColorChange, validateRequestForm, onFilterChange, searchColors, toggleLatestColors, fetchColors, fetchAndRegisterColorsData, setDefaultValues, ColorModuleHeader, ColorModuleTable, ColorModuleRequest, ColorModuleResult, ColorModuleTitle, ColorModuleWelcomeModal }
}

})